import AppStateService from '@ajs/services/AppStateService';
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AccountFeatureFlag } from '@app/core/models/enums/account-feature-flag.enum';
import { UserFeatureFlag } from '@app/core/models/enums/user-feature-flag.enum';
import { LinkService } from '@app/core/services/link.service';
import { combineLatest, filter, map, take } from 'rxjs';

export function redirectOnFeatureFlag(
    redirectRoute: keyof LinkService,
    userFeatureFlags?: UserFeatureFlag[],
    accountFeatureFlags?: AccountFeatureFlag[]
): CanActivateFn {
    return () => {
        const appStateService: AppStateService = inject(AppStateService);
        const linkService: LinkService = inject(LinkService);
        const router: Router = inject(Router);

        return combineLatest([
            appStateService.userLoaded$,
            appStateService.databaseLoaded$
        ]).pipe(
            filter(([userLoaded, dbLoaded]) => Boolean(userLoaded) && Boolean(dbLoaded)),
            take(1),
            map(() => {
                const redirectAllowed = (userFeatureFlags || []).every((name) => appStateService.getUser().hasFeatureEnabled(name)) && 
                    (accountFeatureFlags || []).every((name) => appStateService.getAccount().hasFeatureEnabled(name));
                let linkServiceTarget = linkService[redirectRoute];
                // fallthrough route for any strange keys on linkService.  Generally use the flag to tell
                // whether to go to the original page or block.
                if (typeof linkServiceTarget === 'function') {
                    return !redirectAllowed;
                }
                if (typeof linkServiceTarget === 'string') {
                    linkServiceTarget = [linkServiceTarget];
                }
                if (redirectAllowed) {
                    return router.createUrlTree(linkServiceTarget);
                }
                return true;
            })
        );
    };
}

export function redirectOnFailedFeatureFlag(
    redirectRoute: keyof LinkService,
    userFeatureFlags?: UserFeatureFlag[],
    accountFeatureFlags?: AccountFeatureFlag[]
): CanActivateFn {
    return () => {
        const appStateService: AppStateService = inject(AppStateService);
        const router: Router = inject(Router);
        const linkService: LinkService = inject(LinkService);

        return combineLatest([
            appStateService.userLoaded$,
            appStateService.databaseLoaded$
        ]).pipe(
            filter(([userLoaded, dbLoaded]) => Boolean(userLoaded) && Boolean(dbLoaded)),
            take(1),
            map(() => {
                const originalAllowed = (userFeatureFlags || []).every((name) => appStateService.getUser().hasFeatureEnabled(name)) &&
                    (accountFeatureFlags || []).every((name) => appStateService.getAccount().hasFeatureEnabled(name));
                let linkServiceTarget = linkService[redirectRoute];

                // fallthrough route for any strange keys on linkService.  Generally use the flag to tell
                // whether to go to the original page or block.
                if (typeof linkServiceTarget === 'function') {
                    return originalAllowed;
                }
                if (typeof linkServiceTarget === 'string') {
                    linkServiceTarget = [linkServiceTarget];
                }
                if (originalAllowed) {
                    return true;
                }
                return router.createUrlTree(linkServiceTarget);
            })
        );
    };
}

// Like redirectOnFailedFeatureFlag, but ORs across the supplied flags
// (account OR user) instead of ANDing. Allows access when *any* of the
// listed user flags is enabled on the current user OR *any* of the listed
// account flags is enabled on the current account.
export function redirectOnFailedAccountOrUserFeatureFlag(
    redirectRoute: keyof LinkService,
    userFeatureFlags?: UserFeatureFlag[],
    accountFeatureFlags?: AccountFeatureFlag[]
): CanActivateFn {
    return () => {
        const appStateService: AppStateService = inject(AppStateService);
        const router: Router = inject(Router);
        const linkService: LinkService = inject(LinkService);

        return combineLatest([
            appStateService.userLoaded$,
            appStateService.databaseLoaded$
        ]).pipe(
            filter(([userLoaded, dbLoaded]) => Boolean(userLoaded) && Boolean(dbLoaded)),
            take(1),
            map(() => {
                const originalAllowed = (userFeatureFlags || []).some((name) => appStateService.getUser().hasFeatureEnabled(name)) ||
                    (accountFeatureFlags || []).some((name) => appStateService.getAccount().hasFeatureEnabled(name));
                let linkServiceTarget = linkService[redirectRoute];

                if (typeof linkServiceTarget === 'function') {
                    return originalAllowed;
                }
                if (typeof linkServiceTarget === 'string') {
                    linkServiceTarget = [linkServiceTarget];
                }
                if (originalAllowed) {
                    return true;
                }
                return router.createUrlTree(linkServiceTarget);
            })
        );
    };
}

// Account-only pages (no database in the URL) cannot wait on databaseLoaded$ —
// that observable never emits when the user is on /:account_id/databases.
// Same OR-gate as redirectOnFailedAccountOrUserFeatureFlag.
export function redirectOnFailedAccountOrUserFeatureFlagAccountOnly(
    redirectRoute: keyof LinkService,
    userFeatureFlags?: UserFeatureFlag[],
    accountFeatureFlags?: AccountFeatureFlag[]
): CanActivateFn {
    return () => {
        const appStateService: AppStateService = inject(AppStateService);
        const router: Router = inject(Router);
        const linkService: LinkService = inject(LinkService);

        return combineLatest([
            appStateService.userLoaded$,
            appStateService.accountLoaded$
        ]).pipe(
            filter(([userLoaded, accountLoaded]) => Boolean(userLoaded) && Boolean(accountLoaded)),
            take(1),
            map(() => {
                const originalAllowed = (userFeatureFlags || []).some((name) => appStateService.getUser().hasFeatureEnabled(name)) ||
                    (accountFeatureFlags || []).some((name) => appStateService.getAccount().hasFeatureEnabled(name));
                let linkServiceTarget = linkService[redirectRoute];

                if (typeof linkServiceTarget === 'function') {
                    return originalAllowed;
                }
                if (typeof linkServiceTarget === 'string') {
                    linkServiceTarget = [linkServiceTarget];
                }
                if (originalAllowed) {
                    return true;
                }
                return router.createUrlTree(linkServiceTarget);
            })
        );
    };
}


